import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Phone, Calendar, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const AdminContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContacts(token);
  }, [navigate]);

  const fetchContacts = async (token) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce message ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setContacts(contacts.filter(c => c._id !== id));
        toast.success('Message supprimé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '48px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Link to="/admin/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--brand-dark)', textDecoration: 'none', marginBottom: '24px' }}>
          <ArrowLeft size={20} />
          <span>Retour au dashboard</span>
        </Link>

        <h1 style={{ fontSize: '36px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
          Messages Contact
        </h1>
        <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '48px' }}>
          {loading ? 'Chargement...' : `${contacts.length} message(s) reçu(s)`}
        </p>

        {loading ? (
          <p>Chargement...</p>
        ) : contacts.length === 0 ? (
          <div className="network-card" style={{ textAlign: 'center', padding: '64px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>Aucun message pour le moment</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {contacts.map((contact) => (
              <div key={contact._id} className="network-card" style={{ padding: 'var(--spacing-large)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      {contact.nom}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                      <MessageSquare size={16} style={{ color: 'var(--brand-primary)' }} />
                      <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                        {contact.sujet}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(contact._id)}
                    style={{
                      background: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Trash2 size={16} />
                    Supprimer
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Contact
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={16} style={{ color: 'var(--brand-primary)' }} />
                        <a href={`mailto:${contact.email}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                          {contact.email}
                        </a>
                      </div>
                      {contact.telephone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={16} style={{ color: 'var(--brand-primary)' }} />
                          <a href={`tel:${contact.telephone}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                            {contact.telephone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Date
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={16} style={{ color: 'var(--brand-primary)' }} />
                      <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        {new Date(contact.createdAt).toLocaleDateString('fr-FR', { 
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  background: 'var(--bg-subtle)', 
                  padding: '20px', 
                  borderRadius: '12px',
                  borderLeft: '4px solid var(--brand-primary)'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)', marginBottom: '12px', textTransform: 'uppercase' }}>
                    Message
                  </h4>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    {contact.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
